import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useMenu } from '../../context/MenuContext';

const { FiCalendar, FiUsers, FiArrowRight, FiBarChart2, FiClipboard, FiRefreshCw, FiLoader, FiAlertCircle } = FiIcons;

const Dashboard = () => {
  const { weeklyMenus } = useMenu();
  const [recentRegistrations, setRecentRegistrations] = useState([]);
  const [stats, setStats] = useState({
    totalRegistrations: 0,
    totalPersons: 0,
    weeklyRegistrations: 0,
    dailyRegistrations: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // 🔥 SIMPLIFIED: Test basic Supabase connection first
  const testConnection = async () => {
    try {
      console.log('🔍 Testing Supabase connection...');
      
      // Test with a simple query first
      const { data: testData, error: testError } = await supabase
        .from('registrations_a7b9c8d6e5')
        .select('count')
        .limit(1);

      if (testError) {
        console.error('❌ Supabase connection test failed:', testError);
        throw testError;
      }

      console.log('✅ Supabase connection successful');
      return true;
    } catch (err) {
      console.error('💥 Connection test error:', err);
      return false;
    }
  };

  // 🔥 ROBUST: Load data with better error handling
  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log("📊 Dashboard: Loading data...");

      // First test the connection
      const connectionOk = await testConnection();
      if (!connectionOk) {
        throw new Error('Supabase connection failed');
      }

      // Load registrations with error handling
      const { data: registrationsData, error: registrationsError } = await supabase
        .from('registrations_a7b9c8d6e5')
        .select('*')
        .order('registration_date', { ascending: false })
        .limit(100); // Limit to avoid large data loads

      if (registrationsError) {
        console.error('❌ Registration query error:', registrationsError);
        throw registrationsError;
      }

      console.log(`✅ ${registrationsData?.length || 0} registrations loaded`);

      // Set recent registrations
      setRecentRegistrations(registrationsData?.slice(0, 5) || []);

      // Calculate statistics
      if (registrationsData && registrationsData.length > 0) {
        const totalPersons = registrationsData.reduce((sum, reg) => sum + (reg.persons || 0), 0);
        const weeklyRegistrations = registrationsData.filter(reg => 
          reg.day && (reg.day.includes('Woche') || reg.day.includes('5 Tage'))
        ).length;
        
        const today = new Date().toISOString().split('T')[0];
        const dailyRegistrations = registrationsData.filter(
          reg => reg.registration_date && reg.registration_date.startsWith(today)
        ).length;

        setStats({
          totalRegistrations: registrationsData.length,
          totalPersons,
          weeklyRegistrations,
          dailyRegistrations
        });
      } else {
        // Reset stats if no data
        setStats({
          totalRegistrations: 0,
          totalPersons: 0,
          weeklyRegistrations: 0,
          dailyRegistrations: 0
        });
      }

    } catch (err) {
      console.error('💥 Dashboard: Error loading data:', err);
      setError(`Fehler beim Laden der Daten: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Format date with error handling
  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'Kein Datum';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Ungültiges Datum';
      
      return date.toLocaleDateString('de-DE') + ' ' + 
             date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return dateString || 'Kein Datum';
    }
  };

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-gray-600">Übersicht über Menüs und Anmeldungen</p>
        </div>
        <button
          onClick={loadData}
          disabled={isLoading}
          className={`flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl transition-colors ${
            isLoading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <SafeIcon icon={isLoading ? FiLoader : FiRefreshCw} className={isLoading ? 'animate-spin' : ''} />
          <span>Aktualisieren</span>
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center">
          <SafeIcon icon={FiAlertCircle} className="mr-2" />
          <div>
            <p className="font-medium">Verbindungsfehler</p>
            <p className="text-sm">{error}</p>
            <button
              onClick={loadData}
              className="text-sm underline hover:no-underline mt-1"
            >
              Erneut versuchen
            </button>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-2xl shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-500 text-sm font-medium">Anmeldungen</h3>
            <div className="bg-blue-100 p-2 rounded-lg">
              <SafeIcon icon={FiUsers} className="text-blue-500" />
            </div>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold text-gray-800">{stats.totalRegistrations}</span>
            <span className="ml-2 text-sm text-gray-500">gesamt</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-500 text-sm font-medium">Personen</h3>
            <div className="bg-green-100 p-2 rounded-lg">
              <SafeIcon icon={FiBarChart2} className="text-green-500" />
            </div>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold text-gray-800">{stats.totalPersons}</span>
            <span className="ml-2 text-sm text-gray-500">gesamt</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-500 text-sm font-medium">Wochenanmeldungen</h3>
            <div className="bg-purple-100 p-2 rounded-lg">
              <SafeIcon icon={FiCalendar} className="text-purple-500" />
            </div>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold text-gray-800">{stats.weeklyRegistrations}</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-500 text-sm font-medium">Heute</h3>
            <div className="bg-amber-100 p-2 rounded-lg">
              <SafeIcon icon={FiClipboard} className="text-amber-500" />
            </div>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold text-gray-800">{stats.dailyRegistrations}</span>
            <span className="ml-2 text-sm text-gray-500">Anmeldungen</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Current Menus */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">Aktuelle Menüs</h2>
            <Link
              to="/admin/aktuelle-menus"
              className="flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              <span>Alle anzeigen</span>
              <SafeIcon icon={FiArrowRight} className="ml-1" />
            </Link>
          </div>
          
          <div className="space-y-4">
            {Object.values(weeklyMenus).length > 0 ? (
              Object.values(weeklyMenus).map((menu) => (
                <div key={menu.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <img 
                    src={menu.image} 
                    alt={menu.name}
                    className="w-12 h-12 rounded-lg object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/48x48?text=Menü';
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-800">{menu.day}</div>
                    <div className="text-sm text-gray-600 truncate">{menu.name}</div>
                  </div>
                  <div className="text-primary-600 font-bold">
                    CHF {menu.price?.toFixed(2)}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4 text-gray-500">
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <SafeIcon icon={FiLoader} className="animate-spin mr-2" />
                    Menüs werden geladen...
                  </div>
                ) : (
                  'Keine Menüs gefunden'
                )}
              </div>
            )}
          </div>
        </div>

        {/* Recent Registrations */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">Neueste Anmeldungen</h2>
            <Link
              to="/admin/anmeldungen"
              className="flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              <span>Alle anzeigen</span>
              <SafeIcon icon={FiArrowRight} className="ml-1" />
            </Link>
          </div>
          
          <div className="space-y-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <SafeIcon icon={FiLoader} className="text-gray-400 text-2xl animate-spin mr-3" />
                <span className="text-gray-500">Anmeldungen werden geladen...</span>
              </div>
            ) : recentRegistrations.length > 0 ? (
              recentRegistrations.map((registration) => (
                <div key={registration.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-gray-800">{registration.name}</span>
                    <span className="text-xs text-gray-500">
                      {formatDate(registration.registration_date)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {registration.day} - {registration.menu_name}
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      {registration.persons} {registration.persons === 1 ? 'Person' : 'Personen'}
                    </span>
                    <span className="font-medium text-primary-600">
                      CHF {registration.total_price?.toFixed(2)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4 text-gray-500">
                Keine Anmeldungen gefunden
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 bg-white rounded-2xl shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-6">Schnellaktionen</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link
            to="/admin/naechste-menus"
            className="flex items-center space-x-3 p-4 bg-primary-50 rounded-xl hover:bg-primary-100 transition-colors"
          >
            <div className="bg-primary-100 p-2 rounded-lg">
              <SafeIcon icon={FiCalendar} className="text-primary-600" />
            </div>
            <div>
              <div className="font-medium text-gray-800">Nächste Woche planen</div>
              <div className="text-sm text-gray-600">Menüs für kommende Woche bearbeiten</div>
            </div>
          </Link>

          <Link
            to="/admin/aktuelle-menus"
            className="flex items-center space-x-3 p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors"
          >
            <div className="bg-green-100 p-2 rounded-lg">
              <SafeIcon icon={FiClipboard} className="text-green-600" />
            </div>
            <div>
              <div className="font-medium text-gray-800">Aktuelle Menüs</div>
              <div className="text-sm text-gray-600">Diese Woche bearbeiten</div>
            </div>
          </Link>

          <Link
            to="/admin/anmeldungen"
            className="flex items-center space-x-3 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors"
          >
            <div className="bg-blue-100 p-2 rounded-lg">
              <SafeIcon icon={FiUsers} className="text-blue-600" />
            </div>
            <div>
              <div className="font-medium text-gray-800">Anmeldungen</div>
              <div className="text-sm text-gray-600">Alle Anmeldungen einsehen</div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;